import { forwardRef, useCallback } from 'react'
import { Button, ButtonProps, Link, LinkProps } from '@mui/material'
import { NavigateOptions } from 'react-router-dom'
import { useReactNavigate } from '$hooks'

export type LoadedLinkProps = {
  to: string
  layout?: 'link' | 'button'
  openNewTab?: boolean
  isExternalUrl?: boolean
  disabled?: boolean
  navigateFn?: (url: string, options?: NavigateOptions) => void
  navigationOptions?: NavigateOptions
  handleClick?: (event) => void
} & (LinkProps | ButtonProps)

const LoadedLink = forwardRef<HTMLAnchorElement, LoadedLinkProps>(
  (
    {
      children,
      to,
      layout = 'link',
      openNewTab = false,
      isExternalUrl = false,
      disabled = false,
      navigateFn,
      navigationOptions,
      handleClick,
      ...props
    },
    ref
  ) => {
    const reactNavigate = useReactNavigate()
    const navigate = navigateFn ?? reactNavigate

    const onClick = useCallback(
      (event) => {
        if (disabled) {
          event.preventDefault()
          return
        }

        handleClick?.(event)

        // A handleClick that calls preventDefault is claiming the click for itself (e.g. to open a
        // dialog first), so neither the browser nor the router should navigate.
        if (event.defaultPrevented) {
          return
        }

        let shouldUseNewTab = openNewTab

        // If the user is ctrl/command clicking, middle clicking, or the
        // prop forcing a new tab, let the browser handle it naturally
        if (event.ctrlKey || event.metaKey || event.button === 1) {
          shouldUseNewTab = true
        }

        if (!shouldUseNewTab) {
          event.preventDefault()
          navigate(to, navigationOptions)
        }
        // For new tab opens, let the browser's default behavior handle it
      },
      [disabled, handleClick, openNewTab, navigate, to, navigationOptions]
    )

    const href = isExternalUrl ? to : `${window.location.pathname}#${to}`

    return layout === 'link' ? (
      <Link
        // We render as an anchor tag (not component="button") to enable browser
        // link features: right-click "Open in new tab", middle-click, Ctrl/Cmd+click,
        // and proper accessibility.
        href={href}
        target={openNewTab ? '_blank' : undefined}
        rel={openNewTab ? 'noopener noreferrer' : undefined}
        underline="none"
        onClick={onClick}
        {...(props as LinkProps)}
        ref={ref}
        sx={{
          // Normalize anchor tag to behave like a button to maintain consistency
          // with the previous component="button" implementation and prevent styling issues.
          display: 'inline-block',
          verticalAlign: 'middle', // Works in non-flex contexts
          alignSelf: 'center', // Works in flex containers
          textAlign: 'center',
          '&:hover': {
            color: 'inherit',
          },
          ...(disabled && {
            pointerEvents: 'none',
          }),
          ...(props as LinkProps).sx,
        }}
      >
        {children}
      </Link>
    ) : (
      <Button
        component="a"
        href={href}
        target={openNewTab ? '_blank' : undefined}
        rel={openNewTab ? 'noopener noreferrer' : undefined}
        onClick={onClick}
        variant="contained"
        color="info"
        {...(props as ButtonProps)}
        sx={{
          // Override Bootstrap's global a:hover color rule that bleeds through
          // from the AngularJS shell and overrides MUI's contained button text
          // color when rendered as an anchor tag via component="a".
          '&:hover': {
            color: (theme) => {
              const colorKey = props.color ?? 'info'
              return theme.palette[colorKey as string]?.contrastText
            },
          },
          ...(disabled && {
            pointerEvents: 'none',
          }),
          ...props.sx,
        }}
      >
        {children}
      </Button>
    )
  }
)

LoadedLink.displayName = 'LoadedLink'

export default LoadedLink
